import Select, { MultiValue, SingleValue, StylesConfig } from 'react-select';

export type TimeSelectValue = string | number;

export interface TimeSelectOption<T extends TimeSelectValue = TimeSelectValue> {
  value: T;
  label: string;
}

interface BaseTimeSelectProps<T extends TimeSelectValue = TimeSelectValue> {
  inputId: string;
  ariaLabel?: string;
  ariaInvalid?: boolean;
  options: TimeSelectOption<T>[];
  placeholder?: string;
  className?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  noOptionsMessage?: string;
}

interface SingleTimeSelectProps<T extends TimeSelectValue = TimeSelectValue> extends BaseTimeSelectProps<T> {
  isMulti?: false;
  value: T | null | undefined;
  onChange: (value: T | null, option: TimeSelectOption<T> | null) => void;
}

interface MultiTimeSelectProps<T extends TimeSelectValue = TimeSelectValue> extends BaseTimeSelectProps<T> {
  isMulti: true;
  value: T[];
  onChange: (value: T[], option: TimeSelectOption<T>[]) => void;
}

type TimeSelectProps<T extends TimeSelectValue = TimeSelectValue> = SingleTimeSelectProps<T> | MultiTimeSelectProps<T>;

const timeSelectStyles: StylesConfig<TimeSelectOption, boolean> = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    backgroundColor: 'var(--surface)',
    borderColor: state.isFocused ? 'var(--border-focus)' : 'var(--border-color)',
    borderRadius: 'var(--radius-md)',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(71, 156, 156, 0.22)' : 'none',
    color: 'var(--text-primary)',
    fontSize: 'var(--font-size-sm)',
    transition: 'all 0.2s ease',
    ':hover': {
      borderColor: state.isFocused ? 'var(--border-focus)' : 'var(--border-color)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0.35rem 0.85rem',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--text-primary)',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--text-primary)',
    margin: 0,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--text-muted)',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    color: 'var(--text-muted)',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'var(--border-color)',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? 'var(--primary)' : 'var(--text-muted)',
    ':hover': {
      color: 'var(--primary)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--text-muted)',
    ':hover': {
      color: 'var(--text-primary)',
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--surface)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-lg)',
    overflow: 'hidden',
    zIndex: 3000,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 3000,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--primary)'
      : state.isFocused
        ? 'var(--surface-hover)'
        : 'var(--surface)',
    color: state.isSelected ? '#ffffff' : 'var(--text-primary)',
    cursor: 'pointer',
    fontSize: 'var(--font-size-sm)',
    ':active': {
      backgroundColor: 'var(--primary)',
      color: '#ffffff',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: 'var(--text-muted)',
    fontSize: 'var(--font-size-sm)',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'rgba(71, 156, 156, 0.14)',
    borderRadius: 'var(--radius-sm)',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'var(--text-primary)',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'var(--text-muted)',
    ':hover': {
      backgroundColor: 'rgba(71, 156, 156, 0.22)',
      color: 'var(--text-primary)',
    },
  }),
};

function TimeSelect<T extends TimeSelectValue = TimeSelectValue>(props: TimeSelectProps<T>) {
  const {
    inputId,
    ariaLabel,
    options,
    placeholder,
    className,
    isClearable = false,
    isDisabled = false,
    noOptionsMessage = 'No options',
  } = props;

  const commonProps = {
    inputId,
    'aria-label': ariaLabel,
    'aria-invalid': props.ariaInvalid,
    className: ['time-select', className].filter(Boolean).join(' '),
    classNamePrefix: 'time-select',
    options,
    placeholder,
    isClearable,
    isDisabled,
    isSearchable: true,
    menuPortalTarget: typeof document === 'undefined' ? undefined : document.body,
    menuPosition: 'fixed' as const,
    noOptionsMessage: () => noOptionsMessage,
  };

  if (props.isMulti) {
    const selectedOptions = options.filter(option => props.value.includes(option.value));

    return (
      <Select<TimeSelectOption<T>, true>
        {...commonProps}
        isMulti
        value={selectedOptions}
        onChange={(selectedOptions: MultiValue<TimeSelectOption<T>>) => {
          const nextOptions = Array.from(selectedOptions);
          props.onChange(nextOptions.map(option => option.value), nextOptions);
        }}
        styles={timeSelectStyles as unknown as StylesConfig<TimeSelectOption<T>, true>}
      />
    );
  }

  const selectedOption = options.find(option => option.value === props.value) ?? null;

  return (
    <Select<TimeSelectOption<T>, false>
      {...commonProps}
      value={selectedOption}
      onChange={(selectedOption: SingleValue<TimeSelectOption<T>>) => {
        props.onChange(selectedOption?.value ?? null, selectedOption ?? null);
      }}
      styles={timeSelectStyles as unknown as StylesConfig<TimeSelectOption<T>, false>}
    />
  );
}

export default TimeSelect;
